import React, { useState } from 'react';
import Modal, { Field, useModalClose } from './Modal';
import { apiErrorMessage } from '../hooks/useApi';
import { presetsFor, savePreset, accessValueKey } from '../utils/propertyPresets';

/**
 * <PropertyInfoAddModal /> — contractor adds to a property-side job tab, using
 * clickable common answers (PPE, access answers, qualifications, …) plus a
 * "＋ add custom" that's saved for reuse. Returns items[] via onAdd.
 *   sheq / contractor → multi-select chips → one item per selection
 *   access / about / review → a labelled value → single item
 *   equipment → category + system + location + condition → single item
 */
const SECTION_TITLE = {
  about: 'Add property detail', access: 'Add access detail', sheq: 'Add PPE / SHEQ',
  equipment: 'Add equipment / system', review: 'Add review date', contractor: 'Add required qualification',
  work: 'Log completed work',
};

const Chips = ({ options, selected, onToggle }) => (
  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {options.map((o) => {
      const on = selected.includes(o);
      return (
        <button key={o} type="button" onClick={() => onToggle(o)} aria-pressed={on}
          style={{ fontSize: 'var(--cs-text-sm)', fontWeight: 600, borderRadius: 999, padding: '6px 12px', cursor: 'pointer', minHeight: 36,
            border: `1px solid ${on ? 'var(--cw-blue)' : 'var(--cw-border)'}`, background: on ? 'var(--cw-blue)' : 'var(--cw-surface)', color: on ? 'var(--cs-white, #fff)' : 'var(--cw-text-2)' }}>
          {on && <i className="fa-solid fa-check" style={{ marginRight: 5 }} aria-hidden="true" />}{o}
        </button>
      );
    })}
  </div>
);

const PropertyInfoAddModal = ({ section, onAdd, onClose }) => {
  // Multi-select sections (PPE, qualifications).
  const isMulti = section === 'sheq' || section === 'contractor';
  const multiKey = section === 'sheq' ? 'ppe' : 'qualification';
  const [opts, setOpts] = useState(() => (isMulti ? presetsFor(multiKey) : []));
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState('');

  // Single-value sections.
  const [label, setLabel] = useState('');
  const [value, setValue] = useState('');
  const [detail, setDetail] = useState('');
  const [category, setCategory] = useState('Fall arrest');
  const [condition, setCondition] = useState('Good');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const dirty = !!(selected.length || custom.trim() || label.trim() || value.trim() || detail.trim());

  const labelKey = section === 'access' ? 'accessLabel' : section === 'about' ? 'aboutLabel' : section === 'review' ? 'reviewLabel' : null;
  const labelOptions = labelKey ? presetsFor(labelKey) : [];
  const valueKey = section === 'access' ? accessValueKey(label) : null;
  const valueOptions = valueKey ? presetsFor(valueKey) : [];

  const toggle = (o) => setSelected((s) => (s.includes(o) ? s.filter((x) => x !== o) : [...s, o]));
  const addCustomChip = () => {
    const v = custom.trim();
    if (!v) return;
    setOpts(savePreset(multiKey, v));
    setSelected((s) => (s.includes(v) ? s : [...s, v]));
    setCustom('');
  };

  const submit = async () => {
    let items = [];
    setError(null);
    if (isMulti) {
      if (!selected.length) { setError('Pick at least one'); return; }
      const lbl = section === 'sheq' ? 'PPE' : 'Qualification';
      items = selected.map((v) => ({ label: lbl, value: v }));
    } else if (section === 'equipment') {
      if (!label.trim()) { setError('Name the system'); return; }
      items = [{ label: label.trim(), value: condition, detail: [category, detail.trim()].filter(Boolean).join(' · ') }];
    } else if (section === 'work') {
      if (!label.trim()) { setError('Describe the work done'); return; }
      items = [{ label: label.trim(), value, detail: detail.trim() || null }];
    } else {
      if (!label.trim()) { setError('Choose a field'); return; }
      if (section === 'review' && !value) { setError('Pick the date'); return; }
      if (labelKey) savePreset(labelKey, label.trim());
      if (valueKey && value.trim()) savePreset(valueKey, value.trim());
      items = [{ label: label.trim(), value: value.trim(), detail: detail.trim() || null }];
    }
    // onAdd may be async; if it throws, stay open so nothing typed is lost.
    setBusy(true);
    try { await onAdd(items); onClose(); }
    catch (e) { setError(apiErrorMessage(e, "Couldn't add that — try again")); }
    finally { setBusy(false); }
  };
  const isLabelField = !isMulti;

  return (
    <Modal
      title={SECTION_TITLE[section] || 'Add information'}
      maxWidth={480}
      dirty={dirty}
      onClose={onClose}
      footer={<>
        <CancelButton />
        <button type="button" className="cw-btn cw-btn-primary" onClick={submit} disabled={busy} aria-busy={busy || undefined}><i className="fa-solid fa-plus" aria-hidden="true" /> {busy ? 'Adding…' : 'Add'}</button>
      </>}
    >
      <p className="cw-meta" style={{ margin: '0 0 12px' }}>
        You know this site — add what the property record is missing. Common answers are one tap.
      </p>

      {isMulti && (
        <>
          <Field label={section === 'sheq' ? 'PPE required (tap all that apply)' : 'Required qualifications (tap all that apply)'} error={error}>
            <Chips options={opts} selected={selected} onToggle={toggle} />
          </Field>
          <Field label="Add another">
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="cw-input" style={{ flex: 1 }} value={custom} placeholder={section === 'sheq' ? 'e.g. Gas monitor' : 'e.g. NVQ Level 2'}
                onChange={(e) => setCustom(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomChip(); } }} />
              <button type="button" className="cw-btn cw-btn-secondary" onClick={addCustomChip}>Add</button>
            </div>
          </Field>
        </>
      )}

      {(section === 'about' || section === 'access' || section === 'review') && (
        <>
          <Field label="Field" required error={isLabelField && !label.trim() ? error : null}>
            <select value={labelOptions.includes(label) ? label : (label ? '__custom__' : '')} onChange={(e) => setLabel(e.target.value === '__custom__' ? '' : e.target.value)}>
              <option value="">Choose…</option>
              {labelOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              <option value="__custom__">＋ Custom…</option>
            </select>
            {!labelOptions.includes(label) && (
              <input className="cw-input" style={{ marginTop: 8 }} aria-label="Custom field name" value={label} placeholder="Custom field name" onChange={(e) => setLabel(e.target.value)} />
            )}
          </Field>
          <Field label={section === 'review' ? 'Date' : 'Answer'} required={section === 'review'} error={section === 'review' && label.trim() && !value ? error : null}>
            {section === 'review' ? (
              <input type="date" value={value} onChange={(e) => setValue(e.target.value)} />
            ) : valueOptions.length ? (
              <>
                <Chips options={valueOptions} selected={value ? [value] : []} onToggle={(o) => setValue(value === o ? '' : o)} />
                <input className="cw-input" style={{ marginTop: 8 }} aria-label="Custom answer" value={valueOptions.includes(value) ? '' : value} placeholder="…or type a custom answer" onChange={(e) => setValue(e.target.value)} />
              </>
            ) : (
              <input value={value} placeholder="Answer" onChange={(e) => setValue(e.target.value)} />
            )}
          </Field>
        </>
      )}

      {section === 'work' && (
        <>
          <Field label="Work done" required error={error}><input value={label} placeholder="e.g. LOLER thorough examination — 6 anchors" onChange={(e) => setLabel(e.target.value)} /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }} className="cw-field-grid">
            <Field label="Date"><input type="date" value={value} onChange={(e) => setValue(e.target.value)} /></Field>
            <Field label="Outcome"><input value={detail} placeholder="e.g. Pass — certificate issued" onChange={(e) => setDetail(e.target.value)} /></Field>
          </div>
        </>
      )}

      {section === 'equipment' && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(180px, 100%), 1fr))', gap: 12 }} className="cw-field-grid">
            <Field label="Category">
              <select value={category} onChange={(e) => setCategory(e.target.value)}>
                {presetsFor('equipmentCategory').map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
            <Field label="Condition">
              <select value={condition} onChange={(e) => setCondition(e.target.value)}>
                {presetsFor('condition').map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </Field>
          </div>
          <Field label="System / equipment" required error={error}><input value={label} placeholder="e.g. BMU restraint anchors" onChange={(e) => setLabel(e.target.value)} /></Field>
          <Field label="Location"><input value={detail} placeholder="e.g. Level 12 roof — north face" onChange={(e) => setDetail(e.target.value)} /></Field>
        </>
      )}
    </Modal>
  );
};

const CancelButton = () => {
  const requestClose = useModalClose();
  return <button type="button" className="cw-btn cw-btn-secondary" onClick={requestClose}>Cancel</button>;
};

export default PropertyInfoAddModal;
